import { useState } from 'react';

import { Box, Button, Flex, Text } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { isInitialQueryLoading } from '@r-client/shared/util/core';

import { useLiveSectorTagsQuery } from '../../../graphql/live-sector-tags-query';
import { getSectorTags } from '../../../utils';
import { SectorChipsRow } from './sector-chips-row/sector-chips-row';
import { SectorChipsSkeleton } from './sector-chips-skeleton/sector-chips-skeleton';
import { SectorMenu } from './sector-menu/sector-menu';
import type { ISectorFilterProps } from './types';

/**
 * Loads the sector tags and offers them two ways: a dropdown on mobile
 * (beside the sort control, per the design) and the scrolling chip row from
 * `md` up.
 *
 * Both render and one is hidden with `display` rather than picked via
 * `useBreakpointValue` — the page is server-rendered and that hook has no
 * answer on the first pass, so the wrong control would flash before swapping.
 * The selection lives here rather than in either child so the two copies
 * can't drift apart when the viewport crosses `md`.
 *
 * Only sectors that have at least one live deal come back (see
 * `live-sector-tags.graphql`), so every chip is guaranteed to filter the grid to
 * a non-empty result.
 *
 * `observer` is required: the query model exposes `data`/`isLoading`/`isRejected`
 * as MobX computeds, so without it the component would read them once and never
 * re-render when the request settles.
 */
export const SectorFilter = observer(function SectorFilter({
  onSelectedSlugChange,
}: ISectorFilterProps) {
  const sectorsQuery = useLiveSectorTagsQuery();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const handleSelect = (slug: string | null) => {
    setSelectedSlug(slug);
    onSelectedSlugChange?.(slug);
  };

  if (isInitialQueryLoading(sectorsQuery)) {
    return <SectorChipsSkeleton />;
  }

  if (sectorsQuery.isRejected) {
    // A page-level `QueryError` would stack a centred heading and button inside
    // a 40px control row, so the failure is reported in place instead.
    return (
      <Flex align="center" gap="2" py="2.5">
        <Text size="body-sm-md" variant="text-secondary">
          Sectors could not be loaded
        </Text>
        <Button
          variant="naked"
          size="sm"
          onClick={() => sectorsQuery.refetch()}
        >
          Try again
        </Button>
      </Flex>
    );
  }

  const sectors = getSectorTags(sectorsQuery.data);

  // An empty list would leave a lone "All" chip filtering nothing.
  if (!sectors.length) {
    return null;
  }

  return (
    <>
      <Box display={{ base: 'block', md: 'none' }}>
        <SectorMenu
          sectors={sectors}
          selectedSlug={selectedSlug}
          onSelect={handleSelect}
        />
      </Box>

      <Box display={{ base: 'none', md: 'block' }}>
        <SectorChipsRow
          sectors={sectors}
          selectedSlug={selectedSlug}
          onSelect={handleSelect}
        />
      </Box>
    </>
  );
});
